import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/redux/store';
import axios from 'axios';
import { z } from 'zod';
import { axiosInstance } from '@/services/api/utils/axiosInstance';
import { apiPath } from '@/redux/apiPath';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { extractAxiosErrorMessage } from '@/utils/getErrorMessage/getErrorMessage.utils';
import { userSchema } from '@/models/userSchema';
import { CreateUserPayload, User, UserPrivilege } from './users.types';
import {
  USERS_FETCHING_ERROR_PREFIX,
  USER_CREATION_ERROR_PREFIX,
  USER_REMOVAL_ERROR_PREFIX,
  USER_PRIVILEGES_UPDATE_ERROR_PREFIX,
  USER_UPDATE_ERROR_PREFIX,
} from './users.constants';

// Type for project privilege operations
export interface ProjectPrivilegePayload {
  privilegeType: 'READ_PROJECT';
  login: string;
}

// Async thunk to fetch users from the API
export const fetchUsers = createAsyncThunk<
  User[], // Return type of the payload creator
  void, // First argument to the payload creator (not needed here)
  { rejectValue: string; state: RootState } // Types for ThunkAPI
>('users/fetchUsers', async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(apiPath.getAllUsers(), {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Validate response
    const isDataValid = validateDataUsingZodSchema(response.data, z.array(userSchema));
    if (!isDataValid) {
      throw new Error('Invalid user data received from server');
    }

    return response.data as User[];
  } catch (error: unknown) {
    let errorMessage = USERS_FETCHING_ERROR_PREFIX;
    if (error instanceof Error) {
      if (axios.isAxiosError(error)) {
        errorMessage += extractAxiosErrorMessage(error);
      } else {
        errorMessage += error.message;
      }
    } else if (typeof error === 'string') {
      errorMessage += error;
    }
    return rejectWithValue(errorMessage);
  }
});

// Create user thunk
export const createUser = createAsyncThunk<
  User, // Return type
  CreateUserPayload, // Argument type
  { rejectValue: string; state: RootState }
>('users/createUser', async (userData, { rejectWithValue }) => {
  try {
    // Create form data
    const formData = new URLSearchParams();
    formData.append('name', userData.name);
    formData.append('surname', userData.surname);
    formData.append('password', userData.password);

    if (userData.email) {
      formData.append('email', userData.email);
    }

    // Always use default privileges since we're removing custom privilege setting
    formData.append('defaultPrivileges', 'true');

    // Make the API call
    const response = await axiosInstance.post(
      apiPath.createUser(userData.login),
      formData.toString(),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      },
    );

    // Validate response
    const isDataValid = validateDataUsingZodSchema(response.data, userSchema);
    if (!isDataValid) {
      throw new Error('Invalid user data received from server');
    }

    return response.data as User;
  } catch (error: unknown) {
    let errorMessage = USER_CREATION_ERROR_PREFIX;

    if (error instanceof Error) {
      if (axios.isAxiosError(error)) {
        errorMessage += extractAxiosErrorMessage(error);
      } else {
        errorMessage += error.message;
      }
    } else if (typeof error === 'string') {
      errorMessage += error;
    }

    return rejectWithValue(errorMessage);
  }
});

// Remove user thunk
export const removeUser = createAsyncThunk<
  string, // Return type (user login)
  string, // Argument type (user login)
  { rejectValue: string; state: RootState }
>('users/removeUser', async (login, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(apiPath.removeUser(login));
    return login; // Return the user login to identify removed user in reducer
  } catch (error: unknown) {
    let errorMessage = USER_REMOVAL_ERROR_PREFIX;

    if (error instanceof Error) {
      if (axios.isAxiosError(error)) {
        errorMessage += extractAxiosErrorMessage(error);
      } else {
        errorMessage += error.message;
      }
    } else if (typeof error === 'string') {
      errorMessage += error;
    }

    return rejectWithValue(errorMessage);
  }
});

// Update user privileges thunk
export const updateUserPrivileges = createAsyncThunk<
  User, // Return type
  { login: string; privileges: UserPrivilege[]; isAdmin?: boolean; isCurator?: boolean }, // Argument type
  { rejectValue: string; state: RootState }
>(
  'users/updateUserPrivileges',
  async ({ login, privileges, isAdmin = false, isCurator = false }, { rejectWithValue }) => {
    try {
      // Convert array of privileges to the format expected by the API
      const privilegesPayload: Record<string, boolean> = {};

      // Always explicitly set both role privileges - only one can be true at a time
      privilegesPayload.IS_ADMIN = isAdmin;
      privilegesPayload.IS_CURATOR = isCurator;

      // Explicitly set full read access privilege to false by default
      // This ensures it is properly revoked when unchecked
      privilegesPayload['READ_PROJECT:*'] = false;

      privileges.forEach(privilege => {
        // Create key in format
        const key = privilege.objectId
          ? `${privilege.privilegeType}:${privilege.objectId}`
          : privilege.privilegeType;
        privilegesPayload[key] = true;
      });

      // Create the full payload
      const fullPayload = { privileges: privilegesPayload };

      const response = await axiosInstance.patch(apiPath.updateUserPrivileges(login), fullPayload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Validate response
      const isDataValid = validateDataUsingZodSchema(response.data, userSchema);
      if (!isDataValid) {
        throw new Error('Invalid user data received from server');
      }

      return response.data as User;
    } catch (error: unknown) {
      let errorMessage = USER_PRIVILEGES_UPDATE_ERROR_PREFIX;

      if (error instanceof Error) {
        if (axios.isAxiosError(error)) {
          errorMessage += extractAxiosErrorMessage(error);
        } else {
          errorMessage += error.message;
        }
      } else if (typeof error === 'string') {
        errorMessage += error;
      }

      return rejectWithValue(errorMessage);
    }
  },
);

// Update user thunk
export const updateUser = createAsyncThunk<
  User, // Return type
  { login: string; userData: Partial<User> }, // Argument type
  { rejectValue: string; state: RootState }
>('users/updateUser', async ({ login, userData }, { rejectWithValue }) => {
  try {
    // Wrap userData in a "user" object as required by the API
    const payload = { user: userData };

    // Make the API call
    const response = await axiosInstance.patch(apiPath.updateUser(login), payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Validate response
    const isDataValid = validateDataUsingZodSchema(response.data, userSchema);
    if (!isDataValid) {
      throw new Error('Invalid user data received from server');
    }

    return response.data as User;
  } catch (error: unknown) {
    let errorMessage = USER_UPDATE_ERROR_PREFIX;

    if (error instanceof Error) {
      if (axios.isAxiosError(error)) {
        errorMessage += extractAxiosErrorMessage(error);
      } else {
        errorMessage += error.message;
      }
    } else if (typeof error === 'string') {
      errorMessage += error;
    }

    return rejectWithValue(errorMessage);
  }
});
